import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import {
  CustomLabel,
  TimesheetLabel
} from 'modules/timesheet-labels/labelSchema';
import { TimeSignalClassification, TimeSignalSource } from 'graphql/types';
import { NoteFormData } from '../../common/noteSchema';
import {
  defaultDateTime,
  mapCustomLabelsToFormFields,
  mapTimesheetLabelsToFormFields
} from '../../common/utils';

type UseNoteFormDefaultValuesProps = {
  timesheetLabels: TimesheetLabel[];
  customLabels: CustomLabel[];
  initialValues?: NoteFormData;
  sources: TimeSignalSource[];
};

export const useNoteFormDefaultValues = ({
  initialValues,
  timesheetLabels,
  customLabels,
  sources
}: UseNoteFormDefaultValuesProps): NoteFormData => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  return useMemo(() => {
    const {
      id,
      note,
      startTime,
      endTime,
      duration,
      date,
      customLabels: customLabelsFromFields,
      classification,
      labelValuesInferenceId,
      timesheetLabels: timesheetLabelFormFields
    } = initialValues || {};
    const localDate = defaultDateTime();
    return {
      id: id ?? '',
      note: note ?? '',
      startTime: startTime ?? null,
      endTime: endTime ?? null,
      duration: duration ?? 0,
      labelValuesInferenceId: labelValuesInferenceId,
      timesheetLabels:
        timesheetLabelFormFields ??
        mapTimesheetLabelsToFormFields(
          isSmartSuggestionEnabled,
          timesheetLabels
        ),
      customLabels:
        customLabelsFromFields ??
        mapCustomLabelsToFormFields(isSmartSuggestionEnabled, customLabels),
      date:
        date ??
        DateTime.local(
          localDate.year,
          localDate.month,
          localDate.day
        ).toJSDate(),
      classification: classification ?? TimeSignalClassification.Unclassified,
      sources: sources
    };
  }, [
    customLabels,
    initialValues,
    isSmartSuggestionEnabled,
    sources,
    timesheetLabels
  ]);
};
